<template>
  <div class="bg-gray-100 w-full h-full rounded-lg overflow-hidden shadow-lg">
    <!-- Attachment Menu Modal -->
    <Modal :is-open="showAttachmentMenu" max-width="max-w-[320px] min-h-[200px]">
      <template #form>
        <div class="bg-white rounded-lg p-6 w-72">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t("Send Attachment") }}
          </h3>
          <div class="grid grid-cols-2 gap-4">
            <button class="p-4 bg-gray-100 rounded-lg text-center hover:bg-gray-200" @click="$emit('selectAttachment', 'image')">
              <Image class="w-8 h-8 mx-auto mb-2" />
              {{ $t("Image") }}
            </button>
            <button class="p-4 bg-gray-100 rounded-lg text-center hover:bg-gray-200" @click="$emit('selectAttachment', 'video')">
              <Video class="w-8 h-8 mx-auto mb-2" />
              {{ $t("Video") }}
            </button>
            <button class="p-4 bg-gray-100 rounded-lg text-center hover:bg-gray-200" @click="$emit('selectAttachment', 'audio')">
              <Music class="w-8 h-8 mx-auto mb-2" />
              {{ $t("Audio") }}
            </button>
            <button class="p-4 bg-gray-100 rounded-lg text-center hover:bg-gray-200" @click="$emit('selectAttachment', 'document')">
              <File class="w-8 h-8 mx-auto mb-2" />
              {{ $t("Document") }}
            </button>
          </div>
          <button
            class="mt-4 w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            @click="$emit('update:showAttachmentMenu', false)">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Template Menu Modal -->
    <Modal :is-open="showTemplateMenu" max-width="max-w-[900px]">
      <template #form>
        <div class="bg-white rounded-lg p-6 w-full">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">
              {{ $t("WhatsApp Templates") }}
            </h3>
            <button v-if="!creating_template" class="px-4 py-2 btn-primary w-fit" @click="showCreateTemplate">
              {{ $t("Create Template") }}
            </button>
            <button v-else class="px-4 py-2 btn-cancel w-fit" @click="cancelCreateTemplate">{{ $t("Cancel") }}</button>
          </div>
          <div v-if="!creating_template" class="space-y-4">
            <div v-if="templates_clone.length === 0" class="text-center text-gray-500 py-4">
              {{ $t("No templates_clone available") }}
            </div>
            <div v-else class="space-y-2 max-h-[400px] overflow-y-auto">
              <div v-for="template in templates_clone" :key="template.id" class="p-4 border rounded-lg hover:bg-gray-50">
                <div class="flex justify-between items-start mb-2">
                  <h4 class="font-medium text-gray-800">
                    {{ template.name }}
                  </h4>
                  <span
                    class="text-xs px-2 py-1 rounded-full"
                    :class="template.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'">
                    {{ template.status }}
                  </span>
                </div>
                <div class="space-y-3">
                  <template v-for="component in template.components" :key="component.type">
                    <div v-if="component.type === 'HEADER'" class="font-semibold">
                      {{ component.text }}
                    </div>
                    <div v-if="component.type === 'BODY'" class="text-sm text-gray-600 whitespace-pre-line">
                      {{ component.text }}
                    </div>
                    <div v-if="component.type === 'FOOTER'" class="text-xs text-gray-500">
                      {{ component.text }}
                    </div>
                    <div v-if="component.type === 'BUTTONS'" class="flex gap-2">
                      <button v-for="btn in component.buttons" :key="btn.text" class="text-sm px-3 py-1 border rounded-md hover:bg-gray-50">
                        {{ btn.text }}
                      </button>
                    </div>
                  </template>
                </div>
                <div class="mt-3 flex w-full justify-between">
                  <SubmitButton
                    :is-submitting="is_deleting_template"
                    :label="$t('Delete')"
                    :submitting-label="$t('Deleting...')"
                    default-classes="btn-danger w-fit"
                    @click="deleteTemplate(template)" />
                  <SubmitButton
                    :is-submitting="is_submitting_template"
                    :label="$t('Use Template')"
                    default-classes="btn-primary w-fit"
                    @click="sendTemplate(template)" />
                </div>
              </div>
            </div>
          </div>

          <!-- Create Template Form -->
          <div v-else class="space-y-4">
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t("Template Name") }}</label>
              <input
                v-model="new_template_clone.name"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#128C7E]"
                placeholder="e.g., feedback_2">
              <p class="text-xs text-gray-500 mt-1">
                {{ $t("Use lowercase letters, numbers and underscores only") }}
              </p>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t("Header") }}</label>
              <select
                v-model="new_template_clone.header_type"
                class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]">
                <option value="text">{{ $t("Text") }}</option>
                <option value="image">{{ $t("Image") }}</option>
              </select>
              <input
                v-if="new_template_clone.header_type === 'text'"
                v-model="new_template_clone.header"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                :placeholder="$t('How did we do?')">
              <input
                v-else-if="new_template_clone.header_type === 'image'"
                v-model="new_template_clone.header_value"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                :placeholder="$t('Image URL')">
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t("Body") }}</label>
              <div class="space-y-3">
                <textarea
                  v-model="new_template_clone.body"
                  rows="4"
                  class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                  placeholder="Thank you for visiting us at {{1}} on {{2}}." />
                <p class="text-xs text-gray-500">
                  {{ $t("Use") }} {{ 1 }}, {{ 2 }}, {{ $t("etc. for variables in the text") }}
                </p>
                <p class="text-xs text-gray-500">
                  {{
                    $t(
                      "Pro Tip: Describe how you want your template to look in the text box, then click `Suggest Template` to generate it."
                    )
                  }}"
                </p>

                <!-- Body Variables -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">{{ $t("Example Values for Variables") }}</label>
                  <div v-for="(pair, index) in new_template_clone.body_variables" :key="index" class="flex gap-2">
                    <select
                      v-model="new_template_clone.variable_types[index]"
                      class="flex-1 px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]">
                      <option v-for="type in parameter_types" :key="type.value" :value="type.value">
                        {{ type.label }}
                      </option>
                    </select>
                    <input
                      v-model="pair[0]"
                      type="text"
                      class="flex-1 px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                      :placeholder="'Value for {{' + (index + 1) + '}}'">
                    <input
                      v-model="pair[1]"
                      type="text"
                      class="flex-1 px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                      :placeholder="'Alternative value for {{' + (index + 1) + '}}'">
                    <button
                      v-if="new_template_clone.body_variables.length > 1"
                      class="px-2 py-1 text-red-600 hover:text-red-800"
                      @click="removeBodyVariable(index)">
                      <X class="w-5 h-5" />
                    </button>
                  </div>
                  <button class="text-sm text-[#128C7E] hover:text-[#075E54]" @click="addBodyVariable">
                    + {{ $t("Add Another Variable Pair") }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Button Section -->
            <div class="space-y-2">
              <label class="block text-sm font-medium text-gray-700">{{ $t("Button") }} ({{ $t("Optional") }})</label>
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="new_template_clone.button_text"
                  type="text"
                  class="px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                  :placeholder="$t('Button Text')">
                <input
                  v-model="new_template_clone.button_url"
                  type="text"
                  class="px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                  :placeholder="$t('Button URL')">
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">{{ $t("Language") }}</label>
                <select
                  v-model="new_template_clone.language"
                  class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]">
                  <option v-for="lang in available_languages" :key="lang.code" :value="lang.code">
                    {{ lang.name }}
                  </option>
                </select>
              </div>

              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">{{ $t("Category") }}</label>
                <select
                  v-model="new_template_clone.category"
                  class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]">
                  <option value="UTILITY">{{ $t("Utility") }}</option>
                  <option value="MARKETING">{{ $t("Marketing") }}</option>
                  <option value="AUTHENTICATION">{{ $t("Authentication") }}</option>
                </select>
              </div>
            </div>

            <!-- Interactive Template Options -->
            <div class="space-y-2">
              <div class="flex items-center space-x-2">
                <input
                  id="is_interactive"
                  v-model="new_template_clone.is_interactive"
                  type="checkbox"
                  class="rounded text-[#128C7E] focus:ring-[#128C7E]">
                <label for="is_interactive" class="text-sm font-medium text-gray-700">
                  {{ $t("Make this an interactive template") }}
                </label>
              </div>

              <div v-if="new_template_clone.is_interactive" class="space-y-4 mt-4">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">{{ $t("Interactive Type") }}</label>
                  <select
                    v-model="new_template_clone.interactive_type"
                    class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]">
                    <option value="button">{{ $t("Buttons") }}</option>
                    <option value="list">{{ $t("List") }}</option>
                  </select>
                </div>

                <!-- Button Type Options -->
                <div v-if="new_template_clone.interactive_type === 'button'" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">{{ $t("Buttons") }}</label>
                  <div v-for="(button, index) in new_template_clone.buttons" :key="index" class="flex gap-2 items-center">
                    <input
                      v-model="button.id"
                      type="text"
                      class="flex-1 px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                      :placeholder="$t('Button ID')">
                    <input
                      v-model="button.title"
                      type="text"
                      class="flex-1 px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                      :placeholder="$t('Button Title')">
                    <button
                      v-if="new_template_clone.buttons.length > 1"
                      class="px-2 py-1 text-red-600 hover:text-red-800"
                      @click="removeButton(index)">
                      <X class="w-5 h-5" />
                    </button>
                  </div>
                  <button
                    v-if="new_template_clone.buttons.length < 3"
                    class="text-sm text-[#128C7E] hover:text-[#075E54]"
                    @click="addButton">
                    + {{ $t("Add Button (max 3)") }}
                  </button>
                </div>

                <!-- List Type Options -->
                <div v-if="new_template_clone.interactive_type === 'list'" class="space-y-4">
                  <div
                    v-for="(section, sectionIndex) in new_template_clone.list_sections"
                    :key="sectionIndex"
                    class="space-y-2 border p-4 rounded-lg">
                    <div class="flex justify-between items-center">
                      <label class="block text-sm font-medium text-gray-700">{{ $t("Section") }} {{ sectionIndex + 1 }}</label>
                      <button
                        v-if="new_template_clone.list_sections.length > 1"
                        class="px-2 py-1 text-red-600 hover:text-red-800"
                        @click="removeSection(sectionIndex)">
                        <X class="w-5 h-5" />
                      </button>
                    </div>

                    <input
                      v-model="section.title"
                      type="text"
                      class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                      :placeholder="$t('Section Title')">

                    <div class="space-y-2">
                      <div v-for="(row, rowIndex) in section.rows" :key="rowIndex" class="flex gap-2 items-start">
                        <div class="flex-1 space-y-2">
                          <input
                            v-model="row.id"
                            type="text"
                            class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                            :placeholder="$t('Item ID')">
                          <input
                            v-model="row.title"
                            type="text"
                            class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                            :placeholder="$t('Item Title')">
                          <input
                            v-model="row.description"
                            type="text"
                            class="w-full px-3 py-2 border rounded-md focus:ring-[#128C7E] focus:border-[#128C7E]"
                            :placeholder="$t('Item Description')">
                        </div>
                        <button
                          v-if="section.rows.length > 1"
                          class="px-2 py-1 text-red-600 hover:text-red-800"
                          @click="removeRow(sectionIndex, rowIndex)">
                          <X class="w-5 h-5" />
                        </button>
                      </div>
                      <button
                        v-if="section.rows.length < 10"
                        class="text-sm text-[#128C7E] hover:text-[#075E54]"
                        @click="addRow(sectionIndex)">
                        + {{ $t("Add Item (max 10 per section)") }}"
                      </button>
                    </div>
                  </div>
                  <button
                    v-if="new_template_clone.list_sections.length < 10"
                    class="text-sm text-[#128C7E] hover:text-[#075E54]"
                    @click="addSection">
                    + {{ $t("Add Section (max 10)") }}"
                  </button>
                </div>
              </div>
            </div>

            <div class="flex justify-between w-full space-x-3 mt-6">
              <SubmitButton
                :is-submitting="generating_template"
                :label="$t('Suggest Template')"
                :use-d-n-a-loading="false"
                :submitting-label="$t('Generating...')"
                default-classes="btn-feature w-fit"
                @click="generateTemplate" />
              <SubmitButton
                :is-submitting="is_submitting_template"
                :label="$t('Create Template')"
                :submitting-label="$t('Creating...')"
                default-classes="btn-primary w-fit"
                @click="createTemplate" />
            </div>
          </div>
        </div>
      </template>
    </Modal>

    <!-- Phone Number Modal -->
    <Modal :is-open="showPhoneModal" max-width="max-w-[500px] w-[500px]">
      <template #form>
        <div class="bg-white rounded-lg p-6 w-full">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t("Add Phone Number") }}
          </h3>
          <div v-if="!selected_phone_metric_clone">
            <div class="p-4 text-center text-gray-500">
              {{ $t("Please map the phone_number number metric") }}
              <br>
              <ComboBoxInput
                :value="selected_phone_metric_clone"
                :return-ids="true"
                :name="'phone_metric'"
                :items="
                  $store.state.metrics.filter(
                    (metric) => metric.object_type == 'contact' && (metric.data_type === 'String' || metric.input_type === 'Phone')
                  )
                "
                :value-attr="'id'"
                :label-attr="'name'"
                @update:modelValue="setPhoneNumberMetric" />
              <div class="mt-4">
                <p class="text-sm text-gray-600 mb-2">
                  {{ $t("No phone_number metric found?") }}
                </p>
                <button type="button" class="px-4 py-2 btn-primary w-fit" @click="createPhoneMetric">
                  {{ $t("Create Phone Metric") }}
                </button>
              </div>
            </div>
          </div>
          <div v-else-if="showPhoneInputModal">
            <div class="space-y-4">
              <TextInput
                v-model="selected_contact_phone_clone"
                :type="'text'"
                :auto-focus="true"
                :show-success-check-mark="true"
                :name="'phone_number'"
                :label="$t('Phone Number')"
                :placeholder="$t('Enter phone_number number')" />
              <div class="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  class="px-4 py-2 text-gray-600 hover:text-gray-800"
                  @click="
                    () => {
                      $emit('update:showPhoneInputModal', false)
                      $emit('update:showPhoneModal', false)
                    }
                  ">
                  {{ $t("Cancel") }}
                </button>
                <button type="button" class="px-4 py-2 btn-primary w-fit" @click="confirmPhoneNumber">
                  {{ $t("Confirm") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
    <!-- Template Parameters Modal -->
    <Modal v-model:is-open="show_template_params_modal" max-width="max-w-[500px]">
      <template #form>
        <div class="bg-white rounded-lg p-6 w-full">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t("Enter Template Values") }}
          </h3>
          <div class="space-y-4">
            <div v-for="(param, index) in template_params" :key="index" class="space-y-2">
              <label class="block text-sm font-medium text-gray-700"> {{ $t("Parameter") }} {{ param.index }} </label>
              <input
                v-model="param.value"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#128C7E]"
                :placeholder="$t('Enter value for parameter') + ' ' + param.index">
            </div>
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button class="px-4 py-2 btn-cancel" @click="cancelTemplateParams">{{ $t("Cancel") }}</button>
            <button class="px-4 py-2 btn-primary" @click="confirmTemplateParams">{{ $t("Send Template") }}</button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { X, Image, Video, Music, File } from "lucide-vue-next"
import Modal from "@/components/shared/Modal.vue"
import TextInput from "@/components/shared/TextInput.vue"
import integrationService from "@/services/integration.service"
import { utils } from "@/store/helpers"
import OpenAI from "openai"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import SubmitButton from "@/components/shared/SubmitButton.vue"

export default {
  name: "ChatModals",
  components: {
    SubmitButton,
    TextInput,
    ComboBoxInput,
    X,
    Image,
    Video,
    Music,
    File,
    Modal
  },
  props: {
    showTemplateMenu: {
      type: Boolean,
      default: false
    },
    showAttachmentMenu: {
      type: Boolean,
      default: false
    },
    showPhoneInputModal: {
      type: Boolean,
      default: false
    },
    showPhoneModal: {
      type: Boolean,
      default: false
    },
    selectedContact: {
      type: Object,
      default: () => ({})
    },
    selectedPhoneMetric: {
      type: String,
      default: ""
    },
    selectedContactPhone: {
      type: String,
      default: ""
    },
    selectedChat: {
      type: Object,
      default: () => ({})
    },
    newTemplate: {
      type: Object,
      default: () => ({})
    },
    templates: {
      type: Array,
      default: () => []
    }
  },
  emits: [
    "sendContact",
    "sendTemplate",
    "selectAttachment",
    "updateContact",
    "initiateChat",
    "sendCurrentLocation",
    "update:showImageModal",
    "update:showTemplateMenu",
    "update:showPhoneModal",
    "update:showTemplateParamsModal",
    "update:showPhoneInputModal",
    "update:showAttachmentMenu"
  ],
  data() {
    return {
      selected_phone_metric_clone: null,
      is_deleting_template: false,
      is_submitting_template: false,
      selected_chat_clone: null,
      creating_template: false,
      template_prompt: "",
      generating_template: false,
      openai: null,
      new_template_clone: {
        name: "",
        type: "text",
        category: "MARKETING",
        language: "en_US",
        is_interactive: false,
        buttons: [],
        header: "",
        header_type: "text",
        header_value: "",
        body: "",
        body_variables: [],
        variable_types: [], // Initialize variable_types array
        footer: "",
        button_text: "",
        button_url: "",
        interactive_type: "button",
        list_sections: []
      },
      selected_contact_clone: null,
      templates_clone: [],
      available_languages: [
        { code: "en_US", name: this.$t("English (US)") },
        { code: "en_GB", name: this.$t("English (UK)") },
        { code: "fr_FR", name: this.$t("French") },
        { code: "ar_SA", name: this.$t("Arabic (Saudi Arabia)") }
      ],
      parameter_types: [
        { value: "text", label: this.$t("Text") },
        { value: "image", label: this.$t("Image") },
        { value: "currency", label: this.$t("Currency") },
        { value: "date_time", label: this.$t("Date & Time") }
      ],
      // Shared logic
      show_phone_input_modal_clone: false,
      selected_contact_phone_clone: "",
      show_template_params_modal: false,
      template_params: [],
      selected_template: null
    }
  },
  computed: {
    integration_id() {
      const integration = this.$store.state.integration.integrations.find((integration) => integration.reference === "whatsapp")
      return integration ? integration.id : null
    }
  },
  watch: {
    selectedContact(value) {
      this.selected_contact_clone = value
    },
    selectedPhoneMetric(value) {
      this.selected_phone_metric_clone = value
    },
    selectedContactPhone(value) {
      this.selected_contact_phone_clone = value
    },
    selectedChat(value) {
      this.selected_chat_clone = value
    },
    newTemplate(value) {
      this.new_template_clone = value
    },
    templates(value) {
      this.templates_clone = value
    }
  },
  created() {
    this.selected_contact_clone = this.selectedContact
    this.selected_phone_metric_clone = this.selectedPhoneMetric
    this.selected_contact_phone_clone = this.selectedContactPhone
    this.selected_chat_clone = this.selectedChat
    this.new_template_clone = this.newTemplate
    this.templates_clone = this.templates

    // Initialize OpenAI client
    this.openai = new OpenAI({
      apiKey: import.meta.env.VITE_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true
    })
  },
  methods: {
    async setPhoneNumberMetric(metricId) {
      this.selected_phone_metric_clone = metricId
      const metric = this.$store.state.metrics.find((m) => m.id === metricId)
      if (metric) {
        await this.$store.dispatch("control/setPlatformOptions", {
          metric: metricId,
          object_type: "contact",
          option: "phone_number"
        })
        this.$emit("update:showPhoneInputModal", !utils.Record(this.selected_contact_clone).phone_number)
        if (utils.Record(this.selected_contact_clone).phone_number) {
          this.selected_contact_phone_clone = utils.Record(this.selected_contact_clone).phone_number
          this.$emit("update:showPhoneModal", false)
          this.confirmPhoneNumber()
        }
      }
    },
    getTemplates() {
      integrationService.getWhatsappTemplates(this.integration_id).then((res) => {
        this.templates_clone = res.data.data
      })
    },

    async createPhoneMetric() {
      const metricData = {
        name: "Phone Number",
        object_type: "contact",
        input_type: "Phone",
        data_type: "String",
        metric_class: "custom_metric",
        is_public: true,
        is_nullable: true
      }

      try {
        const response = await this.$store.dispatch("data/addMetric", metricData)
        if (response) {
          this.selected_phone_metric_clone = response.id
          await this.$store.dispatch("control/setPlatformOptions", {
            metric: response,
            object_type: "contact",
            option: "phone_number"
          })
          this.show_phone_input_modal_clone = true
          return true
        }
      } catch (error) {
        console.error("Error creating phone_number metric:", error)
      }
      return false
    },

    confirmPhoneNumber() {
      if (!this.selected_contact_phone_clone) {
        return
      }
      utils.Record(this.selected_contact_clone).updateMetricValue(this.selected_phone_metric_clone, this.selected_contact_phone_clone)
      this.selected_contact_clone.phone_number = this.selected_contact_phone_clone
      this.$emit("update:showPhoneModal", false)
      this.$emit("updateContact", this.selected_contact_clone)
      this.selected_contact_clone = null
      this.selected_contact_phone_clone = ""
    },
    sendTemplate(template) {
      // Extract parameters from template body
      const paramRegex = /{{(\d+)}}/g
      const params = []
      let match

      // Reset template params
      this.template_params = []

      // Find all parameters in body text
      template.components.forEach((component) => {
        if (component.type === "BODY") {
          while ((match = paramRegex.exec(component.text)) !== null) {
            const paramIndex = parseInt(match[1], 10)
            if (!params.includes(paramIndex)) {
              params.push(paramIndex)
              this.template_params.push({
                index: paramIndex,
                value: component.example?.body_text?.[0]?.[paramIndex - 1] || ""
              })
            }
          }
        }
      })

      if (this.template_params.length > 0) {
        this.selected_template = template
        this.show_template_params_modal = true
      } else {
        this.$emit("sendTemplate", template)
        this.$emit("update:showTemplateMenu", false)
      }
    },
    cancelTemplateParams() {
      this.show_template_params_modal = false
      this.template_params = []
      this.selected_template = null
    },

    confirmTemplateParams() {
      if (!this.selected_template) {
        return
      }

      // Create a copy of the template with updated values
      const template = JSON.parse(JSON.stringify(this.selected_template))

      // Update the example values in the template
      template.components.forEach((component) => {
        if (component.type === "BODY") {
          // Sort parameters by index to ensure correct order
          const sortedParams = [...this.template_params].sort((a, b) => a.index - b.index)

          // Set both example values and parameters
          if (!component.example) {
            component.example = {}
          }
          component.example.body_text = [sortedParams.map((param) => param.value)]

          // Add parameters to the component directly
          component.parameters = sortedParams.map((param) => ({
            type: "text",
            text: param.value
          }))
        }
      })

      this.$emit("sendTemplate", template)
      this.$emit("update:showTemplateMenu", false)
      this.show_template_params_modal = false
      this.template_params = []
      this.selected_template = null
    },

    checkMessageStatus(localMessageId, whatsappMessageId) {
      integrationService
        .getWhatsappMessageStatus(this.integration_id, whatsappMessageId)
        .then((response) => {
          const messageIndex = this.selected_chat_clone.messages.findIndex((m) => m.id === localMessageId)
          if (messageIndex !== -1) {
            const status = response.status?.toLowerCase()
            if (status === "delivered" || status === "read") {
              this.selected_chat_clone.messages[messageIndex].status = "delivered"
            } else if (status === "failed") {
              this.selected_chat_clone.messages[messageIndex].status = "failed"
            } else if (status === "sent") {
              // Check again after 5 seconds if not delivered
              setTimeout(() => {
                this.checkMessageStatus(localMessageId, whatsappMessageId)
              }, 5000)
            }
          }
        })
        .catch((error) => {
          console.error("Error checking message status:", error)
          // Retry after 5 seconds on error
          setTimeout(() => {
            this.checkMessageStatus(localMessageId, whatsappMessageId)
          }, 5000)
        })
    },

    showCreateTemplate() {
      this.creating_template = true
    },

    cancelCreateTemplate() {
      this.$emit("update:showTemplateMenu", false)
      this.new_template_clone = {
        name: "",
        category: "MARKETING",
        language: "en_US",
        components: [],
        is_interactive: false,
        buttons: [],
        header: "",
        header_type: "text",
        header_value: "",
        body: "",
        body_variables: [],
        variable_types: [], // Initialize variable_types array
        footer: "",
        button_text: "",
        button_url: "",
        interactive_type: "button",
        list_sections: []
      }
    },

    addBodyVariable() {
      this.new_template_clone.body_variables.push(["", ""])
      this.new_template_clone.variable_types.push("text")
    },

    removeBodyVariable(index) {
      this.new_template_clone.body_variables.splice(index, 1)
      this.new_template_clone.variable_types.splice(index, 1)
    },
    addButton() {
      if (this.new_template_clone.buttons.length < 3) {
        this.new_template_clone.buttons.push({
          id: `button${this.new_template_clone.buttons.length + 1}`,
          title: `Button ${this.new_template_clone.buttons.length + 1}`
        })
      }
    },
    removeButton(index) {
      this.new_template_clone.buttons.splice(index, 1)
    },
    addSection() {
      if (this.new_template_clone.list_sections.length < 10) {
        this.new_template_clone.list_sections.push({
          title: this.$t("Section") + ` ${this.new_template_clone.list_sections.length + 1}`,
          rows: [{ id: "item1", title: "Item 1", description: this.$t("Description 1") }]
        })
      }
    },
    removeSection(index) {
      this.new_template_clone.list_sections.splice(index, 1)
    },
    addRow(sectionIndex) {
      const section = this.new_template_clone.list_sections[sectionIndex]
      if (section.rows.length < 10) {
        section.rows.push({
          id: `item${section.rows.length + 1}`,
          title: `Item ${section.rows.length + 1}`,
          description: this.$t("Description") + ` ${section.rows.length + 1}`
        })
      }
    },
    removeRow(sectionIndex, rowIndex) {
      this.new_template_clone.list_sections[sectionIndex].rows.splice(rowIndex, 1)
    },
    deleteTemplate(template) {
      this.is_deleting_template = true
      integrationService
        .deleteWhatsappTemplate(this.integration_id, { template_name: template.name })
        .then(() => {
          this.templates_clone = this.templates_clone.filter((t) => t.id !== template.id)
          this.is_deleting_template = false
        })
        .catch(() => {
          this.$toast.error(this.$t("Error deleting template") + ` ${template.name}`, { duration: 5000 })
        })
    },

    createTemplate() {
      this.is_submitting_template = true

      // Validation for required fields
      const errors = []

      // Check required fields
      if (!this.new_template_clone.name || !/^[a-z0-9_]+$/.test(this.new_template_clone.name)) {
        errors.push(this.$t("Template name is required and must use only lowercase letters, numbers, and underscores."))
      }
      // MAKE SURE THE NAME IS NOT USED
      if (this.templates_clone.some((template) => template.name === this.new_template_clone.name)) {
        errors.push(this.$t("Template name is already in use."))
      }
      if (!this.new_template_clone.language) {
        errors.push(this.$t("Language is required."))
      }
      if (!this.new_template_clone.category) {
        errors.push(this.$t("Category is required."))
      }
      if (!this.new_template_clone.body) {
        errors.push(this.$t("Body text is required."))
      }
      if (this.new_template_clone.header_type === "text" && !this.new_template_clone.header) {
        errors.push(this.$t("Header text is required for text header type."))
      }
      if (this.new_template_clone.header_type === "image" && !this.new_template_clone.header_value) {
        errors.push(this.$t("Image URL is required for image header type."))
      }

      // Validate example values match placeholders
      const placeholderMatches = this.new_template_clone.body.match(/{{\d+}}/g) || []
      const exampleValues = this.new_template_clone.body_variables.map((variable) => variable[0])

      if (placeholderMatches.length > 0 && placeholderMatches.length !== exampleValues.length) {
        errors.push(this.$t("Number of placeholders in BODY.text does not match the number of example values."))
      }

      // Stop submission if errors exist
      if (errors.length > 0) {
        console.error("Validation errors:", errors)
        this.$toast.error(errors.join(" "), { duration: 5000 })
        this.is_submitting_template = false
        return
      }

      // Ensure the type defaults to 'text' if not explicitly interactive
      const templateType = this.new_template_clone?.type === "interactive" ? "interactive" : "text"

      // Build the BODY component
      const bodyComponent = {
        type: "BODY",
        text: this.new_template_clone.body,
        ...(placeholderMatches.length > 0 && { example: { body_text: [exampleValues] } })
      }

      // Build the template data
      const template_wa_data = {
        name: this.new_template_clone.name,
        language: this.new_template_clone.language,
        category: this.new_template_clone.category,
        type: templateType,
        components: [
          {
            type: "HEADER",
            format: this.new_template_clone.header_type.toUpperCase(),
            ...(this.new_template_clone.header_type === "text"
              ? { text: this.new_template_clone.header }
              : { example: { header_handle: [this.new_template_clone.header_value] } })
          },
          bodyComponent, // Include the BODY component
          ...(this.new_template_clone.footer
            ? [
                {
                  type: "FOOTER",
                  text: this.new_template_clone.footer
                }
              ]
            : []),
          ...(this.new_template_clone.buttons.length > 0
            ? [
                {
                  type: "BUTTONS",
                  buttons: this.new_template_clone.buttons.slice(0, 3).map((button) => ({
                    type: button.type,
                    text: button.text,
                    ...(button.type === "URL" ? { url: button.url } : {})
                  }))
                }
              ]
            : [])
        ].flat()
      }

      // Submit the template via the integration service
      integrationService
        .createWhatsappTemplate(this.integration_id, template_wa_data)
        .then(() => {
          this.resetTemplate()
          this.is_submitting_template = false
          this.getTemplates()
          this.creating_template = false
        })
        .catch((error) => {
          this.is_submitting_template = false
          console.error("Error submitting template:", error.response?.data || error)
          this.$toast.error(this.$t("Error submitting template") + `: ${error.response?.data?.message || error.message}`, {
            duration: 5000
          })
        })
    },

    resetTemplate() {
      this.new_template_clone = {
        name: "",
        type: "text",
        category: "MARKETING",
        language: "en_US",
        components: [],
        is_interactive: false,
        buttons: [],
        header: "",
        header_type: "text",
        header_value: "",
        body: "",
        body_variables: [],
        variable_types: [], // Initialize variable_types array
        footer: "",
        button_text: "",
        button_url: "",
        interactive_type: "button",
        list_sections: []
      }
    },
    async generateTemplate() {
      this.generating_template = true
      try {
        const systemMessage = `You are a WhatsApp Business template expert. Create a template based on the user's request.
              Create a template that follows WhatsApp's guidelines and return it in the exact JSON format specified below.

              Required template structure:
              {
                "name": "template_name_in_snake_case",
                "category": "MARKETING|UTILITY|AUTHENTICATION",
                "language": "en_US",
                "components": [
                  {
                    "type": "HEADER",
                    "format": "TEXT|IMAGE",
                    "text": "Header text if format is TEXT",
                    "example": {
                      "header_handle": ["image_url_if_format_is_IMAGE"]
                    }
                  },
                  {
                    "type": "BODY",
                    "text": "Body text with {{1}} variables",
                    "example": {
                      "body_text": [["variable 1 example"]]
                    }
                  },
                  {
                    "type": "FOOTER",
                    "text": "Footer text"
                  },
                  {
                    "type": "BUTTONS"
                  }
                ],
                "is_interactive": true,
                "buttons": [
                  {
                    "type": "URL",
                    "text": "Click here",
                    "url": "https://example.com"
                  },
                  {
                    "type": "QUICK_REPLY",
                    "text": "Button text"
                  }
                ]
              }

              IMPORTANT RULES:
              1. Return ONLY the JSON object, no additional text or explanation
              2. Ensure the JSON is valid and properly formatted
              3. All property names must be exactly as shown above
              4. The template name must be in snake_case format
              5. Variables in body text must be numbered like {{1}}, {{2}}, etc.
              6. Maximum 3 buttons allowed
              7. Language code must be valid (e.g., en_US, es_ES)
              8. Category must be one of: MARKETING, UTILITY, or AUTHENTICATION
              9. Header format must be either TEXT or IMAGE
              10. If header format is TEXT, include "text" field and omit "example"
              11. If header format is IMAGE, include "example.header_handle" array with image URL
              12. Body text must include at least one variable {{1}}
              13. Body example must be a nested array with example values for each variable
              14. Footer text is required
              15. If including buttons, add BUTTONS component and set is_interactive to true
              16. URL buttons must include type, text, and url
              17. QUICK_REPLY buttons only need type and text
              18. Components must be in order: HEADER, BODY, FOOTER, BUTTONS
              19. All text fields must be non-empty strings`

        const userMessage =
          this.template_prompt ||
          this.$t("Create a WhatsApp template for marketing purposes with a header, body with variables, footer, and interactive buttons.")

        const messages = [
          { role: "system", content: systemMessage },
          { role: "user", content: userMessage }
        ]

        try {
          const response = await this.openai.chat.completions.create({
            model: "gpt-4",
            messages: messages,
            temperature: 0.7,
            max_tokens: 2000
          })

          if (!response.choices?.[0]?.message?.content) {
            throw new Error(this.$t("Invalid response from OpenAI API"))
          }

          const templateString = response.choices[0].message.content
          const template = JSON.parse(templateString)

          // Validate required template structure
          if (!template.name || !template.category || !template.language || !Array.isArray(template.components)) {
            throw new Error(this.$t("Invalid template structure: missing required fields"))
          }

          // Initialize new template with default values
          this.new_template_clone = {
            name: template.name,
            category: template.category,
            language: template.language,
            components: template.components,
            is_interactive: template.is_interactive || false,
            buttons: [],
            header: "",
            header_type: "text",
            header_value: "",
            body: "",
            body_variables: [],
            variable_types: [], // Initialize variable_types array
            footer: "",
            button_text: "",
            button_url: "",
            interactive_type: "button",
            list_sections: []
          }

          // Extract components
          const headerComponent = template.components.find((c) => c.type === "HEADER")
          const bodyComponent = template.components.find((c) => c.type === "BODY")
          const footerComponent = template.components.find((c) => c.type === "FOOTER")

          // Process header
          if (headerComponent) {
            this.new_template_clone.header_type = headerComponent.format?.toLowerCase() || "text"
            if (headerComponent.format === "TEXT") {
              this.new_template_clone.header = headerComponent.text || ""
            } else if (headerComponent.format === "IMAGE") {
              this.new_template_clone.header_value = headerComponent.example?.header_handle?.[0] || ""
            }
          }

          // Process body and variables
          if (bodyComponent) {
            this.new_template_clone.body = bodyComponent.text || ""

            // Extract variables from body text
            const variables = bodyComponent.example?.body_text?.[0] || []

            this.new_template_clone.body_variables = variables.map((value, _) => {
              return [value, ""]
            })

            // Initialize variable_types array with default type "text"
            this.new_template_clone.variable_types = variables.map(() => "text")
          }

          // Process footer
          if (footerComponent) {
            this.new_template_clone.footer = footerComponent.text || ""
          }

          // Process buttons
          if (template.buttons?.length > 0) {
            this.new_template_clone.is_interactive = true
            this.new_template_clone.buttons = template.buttons
              .slice(0, 3) // Ensure maximum 3 buttons
              .map((button) => ({
                id: button.text,
                title: button.text,
                type: button.type,
                text: button.text,
                url: button.url || ""
              }))
          } else {
            // Use default button values if none are provided by the template
            this.new_template_clone.buttons = [
              {
                type: "URL",
                text: this.$t("View Products"),
                url: "https://example.com/products"
              },
              {
                type: "QUICK_REPLY",
                text: this.$t("Contact Us"),
                url: ""
              }
            ]
          }
        } catch (error) {
          console.error("Error in OpenAI API call:", error)
          throw new Error(this.$t("Failed to generate template") + `: ${error.message}`)
        }
      } catch (error) {
        console.error("Error generating template:", error)
        throw error
      } finally {
        this.generating_template = false
      }
    }
  }
}
</script>

<style scoped>
.bg-whatsapp-chat {
  background-color: #e5ddd5;
  background-image: url("@/assets/whatsapp-texture.jpg");
  background-repeat: repeat;
  background-size: 410px auto;
}

.template-preview-enter-active,
.template-preview-leave-active {
  transition: all 0.3s ease;
}

.template-preview-enter-from,
.template-preview-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
